import React from "react";
import { Icon } from "@iconify/react";
import {
  campaignTemplateHasButtons,
  campaignTemplateHasHeader,
  countCampaignTemplateVariables,
  getCampaignTemplateBodyText,
  getCampaignTemplateCategory,
  getCampaignTemplateHeaderFormat,
  getCampaignTemplateLanguage,
  getCampaignTemplateName,
  getCampaignTemplateStatus,
  getCampaignTemplateTypeLabel,
  templateStatusClass,
} from "../../../utils/whatsappCampaign";

function StatusBadge({ status }) {
  return (
    <span
      className={`wa-campaign-badge wa-campaign-badge--status ${templateStatusClass(status)}`}
    >
      {status || "—"}
    </span>
  );
}

function TypeBadge({ label }) {
  return (
    <span className="wa-campaign-badge wa-campaign-badge--type">{label}</span>
  );
}

function TagBadge({ label, tone = "muted" }) {
  return (
    <span
      className={`wa-campaign-badge wa-campaign-badge--tag wa-campaign-badge--${tone}`}
    >
      {label}
    </span>
  );
}

function templateHeaderMeta(template) {
  const format = getCampaignTemplateHeaderFormat(template);
  if (!format) return null;
  if (format === "IMAGE") {
    return { icon: "solar:gallery-linear", label: "Image header" };
  }
  if (format === "VIDEO") {
    return { icon: "solar:videocamera-record-linear", label: "Video header" };
  }
  if (format === "DOCUMENT") {
    return { icon: "solar:document-linear", label: "Document header" };
  }
  if (format === "TEXT") {
    return { icon: "solar:text-field-linear", label: "Text header" };
  }
  return { icon: "solar:text-field-linear", label: "Header" };
}

export default function TemplatePickerCard({
  template,
  selected = false,
  onSelect,
  onUse,
  showUseButton = false,
}) {
  const name = getCampaignTemplateName(template);
  const body = getCampaignTemplateBodyText(template);
  const status = getCampaignTemplateStatus(template);
  const typeLabel = getCampaignTemplateTypeLabel(template);
  const category = getCampaignTemplateCategory(template);
  const language = getCampaignTemplateLanguage(template);
  const varCount = countCampaignTemplateVariables(template);
  const hasHeader = campaignTemplateHasHeader(template);
  const hasButtons = campaignTemplateHasButtons(template);
  const headerMeta = hasHeader ? templateHeaderMeta(template) : null;
  const interactive = typeof onSelect === "function";

  return (
    <article
      className={`wa-campaign-tpl-card ${selected ? "selected" : ""}`}
      onClick={interactive ? () => onSelect(template) : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(template);
              }
            }
          : undefined
      }
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <div className="wa-campaign-tpl-card-head">
        <h3>{name}</h3>
        <StatusBadge status={status} />
      </div>
      <div className="wa-campaign-tpl-card-tags">
        <TypeBadge label={typeLabel} />
        <TagBadge label={category} tone="category" />
        <TagBadge label={language} tone="lang" />
      </div>
      <p className="wa-campaign-tpl-card-body">{body || "No body text"}</p>
      <div className="wa-campaign-tpl-card-meta">
        {hasButtons ? (
          <span>
            <Icon icon="solar:link-round-linear" width={14} />
            Buttons
          </span>
        ) : null}
        {headerMeta ? (
          <span>
            <Icon icon={headerMeta.icon} width={14} />
            {headerMeta.label}
          </span>
        ) : null}
        {varCount > 0 ? (
          <span>
            <Icon icon="solar:code-circle-linear" width={14} />
            {varCount} Variable{varCount === 1 ? "" : "s"}
          </span>
        ) : null}
      </div>
      {showUseButton && typeof onUse === "function" ? (
        <button
          type="button"
          className="wa-campaign-use-btn"
          onClick={(e) => {
            e.stopPropagation();
            onUse(template);
          }}
        >
          Use Template
        </button>
      ) : null}
    </article>
  );
}
